import BigNumber from 'bignumber.js';
import { DraftOptionLeg } from '#/unified-rfqs/ui/rfq-builder/types';

import { Greeks, formatDelta } from '#/unified-rfqs/entities/greeks';
import { UnifiedInstruments } from '#/unified-rfqs/ui/instruments/types';
import { Maybe } from '@paradigm/utils/types';
import { productView } from './leg-product';
import { actionView } from './leg-action';
import { optionInstrumentKindView } from './leg-option-instrument-kind';
import { optionExpirationView } from './option-leg-expiration';
import { strikeView } from './leg-strike';
import { ratioView } from './leg-ratio';
import { instrumentView } from './option-leg-instrument';
import { greeksView } from './option-leg-greeks';
import { quantityView } from './leg-quantity';

export function optionLegView(
  leg: DraftOptionLeg,
  optionInstruments: UnifiedInstruments['optionInstruments'],
  perpFutureInstrument: Maybe<UnifiedInstruments['futureInstruments'][number]>,
  isRemovable: boolean,
  isOrderBook: boolean,
) {
  const preparedInstruments =
    leg.optionKind != null
      ? prepareInstruments(optionInstruments, leg.optionKind)
      : [];

  const { index } = leg;
  const kind = 'OPTION' as const;
  const product = productView(leg, isOrderBook);
  const action = actionView(leg.action, leg.productCode, false);
  const instrumentKind = optionInstrumentKindView(
    leg.optionKind,
    leg.productCode,
  );
  const expiration = optionExpirationView(leg.expiration, preparedInstruments);
  const strike = strikeView(
    leg.expiration,
    leg.strike,
    preparedInstruments,
    perpFutureInstrument,
  );
  const ratio = ratioView(leg.ratio, leg.productCode, isOrderBook);
  const quantity = quantityView(
    leg.quantity,
    leg.productCode,
    isOrderBook,
    false,
  );
  const [instrument, instrument_id] = instrumentView(
    leg.optionKind,
    leg.expiration,
    leg.strike,
    optionInstruments,
  );
  const greeks = greeksView(
    leg.productCode,
    leg.optionKind,
    leg.expiration,
    leg.strike,
    optionInstruments,
  );

  const isValid =
    expiration.isValid &&
    strike.isValid &&
    ratio.isValid &&
    instrumentKind.isValid &&
    (isOrderBook || quantity.isValid);

  const delta = calculateSidedDelta(leg, greeks);

  return {
    index,
    kind,
    product,
    action,
    delta,
    instrumentKind,
    expiration,
    strike,
    ratio,
    isValid,
    isRemovable,
    instrument,
    instrument_id,
    greeks,
    quantity,
    isHedge: false,
  };
}

function prepareInstruments(
  instruments: UnifiedInstruments['optionInstruments'],
  optionKind: 'PUT' | 'CALL',
) {
  const filtered = instruments.filter((ins) => ins.option_kind === optionKind);

  /** Instruments sorted by expiration, then strike, ascending */
  const sorted = filtered.sort((a, b) => {
    if (a.expiration - b.expiration !== 0) return a.expiration - b.expiration;
    return Number.parseFloat(a.strike) - Number.parseFloat(b.strike);
  });

  return sorted;
}

function calculateSidedDelta(draftLeg: DraftOptionLeg, greeks?: Greeks) {
  const sideMultiplier = draftLeg.action === 'BUY' ? 1 : -1;
  const sidedDelta = greeks
    ? BigNumber(sideMultiplier).times(greeks.delta).toString()
    : undefined;

  return formatDelta(sidedDelta);
}
